import { Component } from 'react'
import i18n from '../i18n'

// i18n is what a render error may well have broken, and a boundary that throws
// while rendering its own fallback takes the page down for good. Every string
// therefore carries an English default that t() falls back to.
function tr(key, fallback) {
    try {
        const v = i18n.t(`common:${key}`, { defaultValue: fallback });
        return v && v !== `common:${key}` ? v : fallback;
    } catch {
        return fallback;
    }
}

// A deploy swaps frontend/current to a new release directory, so the chunks the
// currently-open page was built against stop existing. The next lazy route then
// fails to import and React unmounts the whole tree — the "random white screen".
// That case is not an app bug and has an exact fix: reload onto the new build.
export function isStaleChunkError(error) {
    const message = String(error?.message || '');
    return /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|dynamically imported module/i.test(message);
}

// One reload only. If the fresh build throws the same error, something is
// genuinely broken and looping would hide it behind an endless refresh.
const RELOAD_KEY = 'ms:chunk-reloaded';

export default class ErrorBoundary extends Component {
    state = { error: null, componentStack: null };

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        console.error('❌ Render error:', error, info?.componentStack);
        // Kept for the UI: a minified message on its own ("l is not a function")
        // identifies nothing, and this crash is rare enough that asking the
        // reporter to reproduce it with devtools open is not realistic.
        this.setState({ componentStack: info?.componentStack || null });
        if (isStaleChunkError(error) && !sessionStorage.getItem(RELOAD_KEY)) {
            try { sessionStorage.setItem(RELOAD_KEY, '1'); } catch { /* private mode */ }
            window.location.reload();
        }
    }

    componentDidMount() {
        // Only a clean mount means the reload worked. Guard on the error state:
        // when a child throws during the very first render this still runs, and
        // clearing the flag there would re-arm the reload into a refresh loop.
        if (this.state.error) return;
        try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* private mode */ }
    }

    details = () => {
        const { error, componentStack } = this.state;
        return [
            `URL: ${window.location.pathname}${window.location.search}`,
            `Error: ${error?.message || error}`,
            error?.stack ? `Stack:\n${error.stack}` : null,
            componentStack ? `Components:${componentStack}` : null,
        ].filter(Boolean).join('\n\n');
    };

    copyDetails = () => {
        navigator.clipboard?.writeText(this.details());
    };

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;

        if (isStaleChunkError(error)) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center gap-3 p-4" style={{ minHeight: '60vh' }}>
                    <p className="mb-0">{tr('errorNewVersion', 'A new version of the app was released. Refresh to continue.')}</p>
                    <button className="btn btn-primary" onClick={() => window.location.reload()}>
                        {tr('errorRefresh', 'Refresh')}
                    </button>
                </div>
            );
        }

        return (
            <div className="d-flex flex-column align-items-center justify-content-center gap-3 p-4" style={{ minHeight: '60vh' }}>
                <h5 className="mb-0">{tr('errorTitle', 'Something went wrong')}</h5>
                <p className="text-muted text-center mb-0">
                    {tr('errorBody', 'This screen failed to load. Try refreshing — if it keeps happening, go back to the home page.')}
                </p>
                <pre className="border rounded p-2 small text-muted" style={{ maxWidth: 520, maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                    {String(error?.message || error)}
                </pre>
                <div className="d-flex gap-2">
                    <button className="btn btn-primary" onClick={() => window.location.reload()}>
                        {tr('errorRefresh', 'Refresh')}
                    </button>
                    <button className="btn btn-outline-secondary" onClick={() => window.location.assign('/')}>
                        {tr('errorHome', 'Home page')}
                    </button>
                    <button className="btn btn-outline-secondary" onClick={this.copyDetails}>
                        {tr('errorCopyDetails', 'Copy details')}
                    </button>
                </div>
                <details style={{ maxWidth: 520, width: '100%' }}>
                    <summary className="small text-muted">{tr('errorDetails', 'Technical details')}</summary>
                    <pre className="border rounded p-2 small text-muted mt-2" style={{ maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                        {this.details()}
                    </pre>
                </details>
            </div>
        );
    }
}
